import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';

const DEFAULT_LOST_COLOR = '#EBC41D';
const DEFAULT_FOUND_COLOR = '#A12AEB';

function normalizeHexInput(value: string, fallback: string): string {
  const trimmed = value.trim();
  return /^#[0-9A-Fa-f]{6}$/.test(trimmed) ? trimmed.toUpperCase() : fallback;
}

const HomeAnnouncementThemePage: React.FC = () => {
  const [lostColor, setLostColor] = React.useState(DEFAULT_LOST_COLOR);
  const [foundColor, setFoundColor] = React.useState(DEFAULT_FOUND_COLOR);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['home-announcement-theme'],
    queryFn: () => apiService.getHomeAnnouncementThemeConfig(),
  });

  React.useEffect(() => {
    if (!data?.config) return;
    setLostColor(normalizeHexInput(String(data.config.lost_color ?? ''), DEFAULT_LOST_COLOR));
    setFoundColor(normalizeHexInput(String(data.config.found_color ?? ''), DEFAULT_FOUND_COLOR));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateHomeAnnouncementThemeConfig({
        lost_color: lostColor,
        found_color: foundColor,
      }),
    onSuccess: (res) => {
      setSaveMessage(res.message || 'Couleurs enregistrées.');
      setTimeout(() => setSaveMessage(null), 2200);
      void refetch();
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Couleurs perdu / trouvé
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Ces couleurs alimentent le hero, la navbar et les bascules perdu / trouvé sur le site public.
      </Typography>

      {error && <Alert severity="error">Impossible de charger la configuration.</Alert>}
      {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Objet perdu
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  fullWidth
                  label="Couleur hexadécimale"
                  value={lostColor}
                  onChange={(e) => setLostColor(e.target.value)}
                  placeholder="#EBC41D"
                />
                <TextField
                  type="color"
                  value={lostColor}
                  onChange={(e) => setLostColor(e.target.value.toUpperCase())}
                  sx={{ width: 72, minWidth: 72 }}
                  inputProps={{ 'aria-label': 'Couleur objet perdu' }}
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: lostColor,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Objet trouvé
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  fullWidth
                  label="Couleur hexadécimale"
                  value={foundColor}
                  onChange={(e) => setFoundColor(e.target.value)}
                  placeholder="#A12AEB"
                />
                <TextField
                  type="color"
                  value={foundColor}
                  onChange={(e) => setFoundColor(e.target.value.toUpperCase())}
                  sx={{ width: 72, minWidth: 72 }}
                  inputProps={{ 'aria-label': 'Couleur objet trouvé' }}
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: foundColor,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || isLoading}
            >
              Enregistrer
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HomeAnnouncementThemePage;
